import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, App, Select, Tag, Space, Popconfirm, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, LockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { authService } from '../services/auth';
import { UserOptionDto, normalizeUserOptions } from '../dto/reference';
import { getDefaultFinancialPermissionsForRole } from '../modules/users/userFinancialPermissionDefaults';

const ROLE_COLORS: Record<string, string> = {
  ADMIN: 'red',
  OPERATIONS: 'blue',
  ORDER_STAFF: 'green',
  PARTNER: 'default',
};

const ROLES = ['ADMIN', 'OPERATIONS', 'ORDER_STAFF', 'PARTNER'];

type UserManagementUser = UserOptionDto & {
  createdAt?: string;
  deletedAt?: string | null;
};

const UserManagement: React.FC = () => {
  const { t } = useTranslation(['users', 'common']);
  const { message } = App.useApp();
  const [users, setUsers] = useState<UserManagementUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [resetModalVisible, setResetModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<UserManagementUser | null>(null);
  const [resetUserId, setResetUserId] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [resetForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'active' | 'archived' | 'all'>('active');
  const currentUser = authService.getCurrentUser();

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/users', { params: { status: statusFilter } });
      setUsers(normalizeUserOptions(response.data, `users.${statusFilter}`) as UserManagementUser[]);
    } catch (error) {
      message.error(t('users:messages.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const refreshVisibleUsers = (nextVisibleStatus?: 'active' | 'archived' | 'all') => {
    if (nextVisibleStatus && nextVisibleStatus !== statusFilter) {
      setStatusFilter(nextVisibleStatus);
      return;
    }

    fetchUsers();
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const permissionPayload: Record<string, boolean> = {};
      ['canManageOrderCosts', 'canManagePurchaseInvoices', 'canViewMargin'].forEach((field) => {
        if (typeof values[field] === 'boolean') {
          permissionPayload[field] = values[field];
        }
      });
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, {
          name: values.name,
          email: values.email,
          role: values.role,
          ...permissionPayload,
        });
        message.success(t('users:messages.updateSuccess'));
      } else {
        await api.post('/users', { ...values, ...permissionPayload });
        message.success(t('users:messages.createSuccess'));
      }
      setModalVisible(false);
      form.resetFields();
      setEditingUser(null);
      refreshVisibleUsers(!editingUser && statusFilter === 'archived' ? 'active' : undefined);
    } catch (error: any) {
      const msg = error.response?.data?.message || error.response?.data?.error;
      message.error(msg || t('users:messages.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (user: UserManagementUser) => {
    setEditingUser(user);
    form.setFieldsValue({
      name: user.name,
      email: user.email,
      role: user.role,
      canManageOrderCosts: user.canManageOrderCosts === true,
      canManagePurchaseInvoices: user.canManagePurchaseInvoices === true,
      canViewMargin: user.canViewMargin === true,
    });
    setModalVisible(true);
  };

  const handleDelete = async (userId: string) => {
    try {
      await api.delete(`/users/${userId}`);
      message.success(t('users:messages.deleteSuccess'));
      fetchUsers();
    } catch (error: any) {
      const msg = error.response?.data?.message;
      message.error(msg || t('users:messages.deleteFailed'));
    }
  };

  const handleRestore = async (userId: string) => {
    try {
      await api.post(`/users/${userId}/restore`);
      message.success(t('users:messages.restoreSuccess'));
      refreshVisibleUsers(statusFilter === 'archived' ? 'active' : undefined);
    } catch (error: any) {
      const msg = error.response?.data?.message;
      message.error(msg || t('users:messages.restoreFailed'));
    }
  };

  const handleResetPassword = async (values: any) => {
    if (!resetUserId) return;
    setResetting(true);
    try {
      await api.put(`/users/${resetUserId}/reset-password`, {
        newPassword: values.newPassword,
      });
      message.success(t('users:messages.resetSuccess'));
      setResetModalVisible(false);
      resetForm.resetFields();
      setResetUserId(null);
    } catch (error) {
      message.error(t('users:messages.resetFailed'));
    } finally {
      setResetting(false);
    }
  };

  const openResetModal = (userId: string) => {
    setResetUserId(userId);
    resetForm.resetFields();
    setResetModalVisible(true);
  };

  const handleRoleChange = (role: string) => {
    if (!editingUser) {
      form.setFieldsValue(getDefaultFinancialPermissionsForRole(role));
    }
  };

  const columns = [
    {
      title: t('users:name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: UserManagementUser) => (
        <Space>
          <span>{name}</span>
          {record.id === currentUser?.id && (
            <Tag color="blue">{t('users:you')}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: t('users:email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('users:role'),
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag color={ROLE_COLORS[role] || 'default'}>
          {t(`common:userRole.${role}`)}
        </Tag>
      ),
    },
    {
      title: t('users:statusLabel'),
      dataIndex: 'deletedAt',
      key: 'deletedAt',
      render: (deletedAt?: string | null) => (
        <Tag color={deletedAt ? 'default' : 'success'}>
          {deletedAt ? t('users:status.archived') : t('users:status.active')}
        </Tag>
      ),
    },
    {
      title: t('users:financialPermissions.label'),
      key: 'financialPermissions',
      render: (_: any, record: UserManagementUser) => (
        <Space size={[4, 4]} wrap>
          {record.canManageOrderCosts && <Tag>{t('users:financialPermissions.orderCostsShort')}</Tag>}
          {record.canManagePurchaseInvoices && <Tag color="gold">{t('users:financialPermissions.purchaseInvoicesShort')}</Tag>}
          {record.canViewMargin && <Tag color="green">{t('users:financialPermissions.marginShort')}</Tag>}
          {!record.canManageOrderCosts && !record.canManagePurchaseInvoices && !record.canViewMargin && '-'}
        </Space>
      ),
    },
    {
      title: t('users:created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: t('common:actions'),
      key: 'actions',
      render: (_: any, record: UserManagementUser) => (
        <Space>
          {!record.deletedAt && (
            <>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              >
                {t('common:edit')}
              </Button>
              <Button
                type="link"
                icon={<LockOutlined />}
                onClick={() => openResetModal(record.id)}
              >
                {t('users:resetPassword')}
              </Button>
            </>
          )}
          {record.deletedAt ? (
            <Button type="link" onClick={() => handleRestore(record.id)}>
              {t('users:restoreAction')}
            </Button>
          ) : record.id !== currentUser?.id && (
            <Popconfirm
              title={t('users:confirmDelete')}
              onConfirm={() => { void handleDelete(record.id); }}
              okText={t('common:yes')}
              cancelText={t('common:no')}
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                {t('users:archiveAction')}
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h2>{t('users:title')}</h2>
        <Space>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 180 }}
            options={[
              { value: 'active', label: t('users:statusOptions.active') },
              { value: 'archived', label: t('users:statusOptions.archived') },
              { value: 'all', label: t('users:statusOptions.all') },
            ]}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingUser(null);
              form.resetFields();
              form.setFieldsValue({
                role: 'OPERATIONS',
                ...getDefaultFinancialPermissionsForRole('OPERATIONS'),
              });
              setModalVisible(true);
            }}
          >
            {t('users:addUser')}
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
      />

      {/* Create / Edit User Modal */}
      <Modal
        title={editingUser ? t('users:editUser') : t('users:addUser')}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingUser(null);
          form.resetFields();
        }}
        maskClosable={false}
        footer={null}
        width={500}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} onFinishFailed={() => message.warning(t('users:messages.fillRequired'))} scrollToFirstError>
          <Form.Item
            name="name"
            label={t('users:name')}
            rules={[{ required: true, message: t('users:validation.nameRequired') }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label={t('users:email')}
            rules={[
              { required: true, message: t('users:validation.emailRequired') },
              { type: 'email', message: t('users:validation.emailInvalid') },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="role"
            label={t('users:role')}
            rules={[{ required: true, message: t('users:validation.roleRequired') }]}
          >
            <Select onChange={handleRoleChange}>
              {ROLES.map((role) => (
                <Select.Option key={role} value={role}>
                  {t(`common:userRole.${role}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {!editingUser && (
            <Form.Item
              name="password"
              label={t('users:password')}
              rules={[
                { required: true, message: t('users:validation.passwordRequired') },
                { min: 6, message: t('users:validation.passwordMin') },
              ]}
            >
              <Input.Password />
            </Form.Item>
          )}
          <Form.Item label={t('users:financialPermissions.label')} style={{ marginBottom: 8 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Form.Item
                name="canManageOrderCosts"
                valuePropName="checked"
                noStyle
              >
                <Switch />
              </Form.Item>
              <span style={{ marginLeft: 8 }}>{t('users:financialPermissions.orderCosts')}</span>
            </Space>
          </Form.Item>
          <Form.Item style={{ marginBottom: 8 }}>
            <Form.Item
              name="canManagePurchaseInvoices"
              valuePropName="checked"
              noStyle
            >
              <Switch />
            </Form.Item>
            <span style={{ marginLeft: 8 }}>{t('users:financialPermissions.purchaseInvoices')}</span>
          </Form.Item>
          <Form.Item style={{ marginBottom: 24 }}>
            <Form.Item
              name="canViewMargin"
              valuePropName="checked"
              noStyle
            >
              <Switch />
            </Form.Item>
            <span style={{ marginLeft: 8 }}>{t('users:financialPermissions.margin')}</span>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {editingUser ? t('common:save') : t('users:addUser')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        title={t('users:resetPassword')}
        open={resetModalVisible}
        onCancel={() => {
          setResetModalVisible(false);
          setResetUserId(null);
          resetForm.resetFields();
        }}
        maskClosable={false}
        footer={null}
        width={400}
      >
        <Form form={resetForm} layout="vertical" onFinish={handleResetPassword} onFinishFailed={() => message.warning(t('users:messages.fillRequired'))} scrollToFirstError>
          <Form.Item
            name="newPassword"
            label={t('users:newPassword')}
            rules={[
              { required: true, message: t('users:validation.passwordRequired') },
              { min: 6, message: t('users:validation.passwordMin') },
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={resetting}>
              {t('users:resetPassword')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;
